import * as Sentry from '@sentry/react';
import * as React from 'react';

import {
    type AncestorNode,
    type Block,
    type Inline,
    isBlockRangeSelection,
    type TextRange,
} from '@gitbook/slate';
import { Logger } from '@gitbook/util-debug';
import type { List } from '@gitbook/util-immutable';

import type { EditorContainer, NodeDOMAttributes, NodeProps } from '../interfaces';
import { getChildrenRanges, isOnlySelectedNode } from '../utils';
import { FragmentRendererContextProvider } from './FragmentRendererContext';
import { NodeVirtualRenderer } from './NodeVirtualRenderer';
import TextRenderer from './TextRenderer';
import VoidWrapper from './VoidWrapper';
import { useVirtualizedNodeList } from './virtualization';

const logger = Logger('slate:react:node');

interface NodeRendererProps {
    key?: string;
    block: Block | null;
    decorations: List<TextRange>;
    selections: List<TextRange>;
    editor: EditorContainer;
    isFocused: boolean;
    isSelected: boolean;
    isSelectedBlock: boolean;
    isBlockSelection: boolean;
    isOnlyNodeSelected: boolean;
    readOnly: boolean;
    node: Block | Inline;
    parent: AncestorNode;
    ancestors: List<AncestorNode>;
    placeholder?: string;
    horizontalAlignment?: 'left' | 'center' | 'right';
    domRef?: React.RefCallback<Element>;
    nodeErrorFallback?: Sentry.ErrorBoundaryProps['fallback'];
}

/*
 * Component to render a node (block or inline).
 */
const NodeRenderer = React.memo(function MemoNodeRenderer(
    props: NodeRendererProps
): React.ReactElement {
    const { ancestors, parent, nodeErrorFallback } = props;
    const isTopLevelNode = ancestors.size === 1 && parent.object === 'document';
    return isTopLevelNode ? (
        <Sentry.ErrorBoundary fallback={nodeErrorFallback ?? undefined}>
            <NodeRendererImpl {...props} />
        </Sentry.ErrorBoundary>
    ) : (
        <NodeRendererImpl {...props} />
    );
}, areEqual);

const NodeRendererImpl = React.memo(function NodeRendererImpl(props: NodeRendererProps) {
    const {
        block,
        decorations,
        selections,
        editor,
        isFocused,
        isSelected,
        isSelectedBlock,
        isBlockSelection,
        isOnlyNodeSelected,
        readOnly,
        node,
        parent,
        ancestors,
        placeholder,
        horizontalAlignment,
        domRef,
    } = props;
    const { stack, value } = editor;
    const { selection } = value;

    //
    // Decorations and selections
    //
    const allDecorations = decorations.concat(stack.getDecorations(node));
    const childrenAncestors = ancestors.push(node);

    const [childrenDecorations, childrenSelections] = getChildrenRanges(
        node,
        allDecorations,
        selections
    );

    const selectionIndexes = node.getSelectionIndexes(selection, isSelected);

    const virtualized = useVirtualizedNodeList({
        editor,
        nodeList: node.nodes.toArray(),
        parentRef: editor.elementRef,
        virtualizationEnabled: editor.virtualizeChildren,
    });

    const children = virtualized.renderNodes(
        node.object === 'block' ? node : (block ?? value.document),
        (child, vChild, index) => {
            const isChildSelected =
                !!selectionIndexes &&
                selectionIndexes.start <= index &&
                index < selectionIndexes.end;

            const isChildSelectedBlock =
                child.object === 'block' &&
                isBlockRangeSelection(selection) &&
                selection.hasBlock(child, editor.value.document);

            return child.object === 'text' ? (
                <TextRenderer
                    key={child.key}
                    // @ts-expect-error
                    block={node.object === 'block' ? node : block}
                    decorations={childrenDecorations[index]}
                    selections={childrenSelections[index]}
                    editor={editor}
                    node={child}
                    parent={node}
                    placeholder={placeholder}
                    horizontalAlignment={horizontalAlignment}
                />
            ) : vChild.virtual ? (
                <NodeVirtualRenderer
                    key={child.key}
                    node={child}
                    editor={editor}
                    height={vChild.virtualHeight}
                    domRef={vChild.ref}
                />
            ) : (
                <NodeRenderer
                    key={child.key}
                    block={node.object === 'block' ? node : block}
                    decorations={childrenDecorations[index]}
                    selections={childrenSelections[index]}
                    editor={editor}
                    isSelected={isChildSelected}
                    isSelectedBlock={isChildSelectedBlock}
                    isBlockSelection={isBlockSelection}
                    isOnlyNodeSelected={
                        isChildSelected && isOnlySelectedNode(selection, child, node)
                    }
                    isFocused={isFocused && isChildSelected}
                    node={child}
                    parent={node}
                    ancestors={childrenAncestors}
                    readOnly={readOnly}
                    domRef={editor.virtualizeChildren ? vChild.ref : undefined}
                />
            );
        }
    );

    // Attributes that the developer must mix into the element in their
    // custom node renderer component.
    const attributes: NodeDOMAttributes = React.useMemo(
        () => ({
            'data-key': node.key,
            // If it's a block node with inline children, add the proper `dir` attribute
            // for text direction.
            ...(node.object === 'block' && node.isLeafBlock() && node.getTextDirection() === 'rtl'
                ? { dir: 'rtl' }
                : {}),
        }),
        [node, editor]
    );

    // @ts-expect-error
    const nodeProps: NodeProps = {
        node,
        editor,
        parent,
        isFocused,
        isSelected,
        isSelectedBlock,
        isBlockSelection,
        isOnlyNodeSelected,
        attributes,
        domRef,
        selections,
        ancestors,
        children,
        placeholder,
    };

    const element = stack.find('renderNode', nodeProps);
    // @ts-expect-error
    const child = node.isVoid ? <VoidWrapper {...props}>{element}</VoidWrapper> : element;

    // Node can contain Fragments, provide a context here for those fragments.
    if (node.fragments.size > 0) {
        return (
            <FragmentRendererContextProvider editor={editor} nodeProps={nodeProps}>
                {child}
            </FragmentRendererContextProvider>
        );
    }
    return child;
});

/*
 * Should the node avoid updating?
 */
function areEqual(props: NodeRendererProps, nextProps: NodeRendererProps): boolean {
    // If the Editor reference has changed, update.
    if (nextProps.editor !== props.editor) {
        return false;
    }

    // @ts-expect-error
    const shouldUpdate = props.editor.stack.find('shouldNodeComponentUpdate', props, nextProps);

    // If the `Component` has a custom logic to determine whether the component
    // needs to be updated or not, return true if it returns true. If it returns
    // false, we need to ignore it, because it shouldn't be allowed it.
    if (shouldUpdate != null) {
        if (shouldUpdate) {
            return false;
        }

        if (shouldUpdate === false) {
            logger.warning(
                "Returning false in `shouldNodeComponentUpdate` does not disable Slate's internal `shouldComponentUpdate` logic. If you want to prevent updates, use React's `shouldComponentUpdate` instead."
            );
        }
    }

    // If the `readOnly` status has changed, re-render in case there is any
    // user-land logic that depends on it, like nested editable contents.
    if (nextProps.readOnly !== props.readOnly) {
        return false;
    }

    // If the node has changed, update. PERF: There are cases where it will have
    // changed, but it's properties will be exactly the same (eg. copy-paste)
    // which this won't catch. But that's rare and not a drag on performance, so
    // for simplicity we just let them through.
    if (nextProps.node !== props.node) {
        return false;
    }

    // If the selection value of the node or of some of its children has changed,
    // re-render in case there is any user-land logic depends on it to render.
    // if the node is selected update it, even if it was already selected: the
    // selection value of some of its children could have been changed and they
    // need to be rendered again.
    if (nextProps.isSelected || props.isSelected) {
        return false;
    }

    if (nextProps.isFocused || props.isFocused) {
        return false;
    }

    // If this node is a block and the mode of selection changes from block to text or vice versa, update.
    if (props.isBlockSelection !== nextProps.isBlockSelection) {
        return false;
    }

    // If the node is a selected block or has been a selected block, update.
    if (nextProps.isSelectedBlock !== props.isSelectedBlock) {
        return false;
    }

    if (props.node.object === 'block' && props.isBlockSelection) {
        return false;
    }

    // If the decorations have changed, update.
    if (!nextProps.decorations.equals(props.decorations)) {
        return false;
    }

    // If the selections have changed, update.
    if (!nextProps.selections.equals(props.selections)) {
        return false;
    }

    // Otherwise, don't update.
    return true;
}

export default NodeRenderer;
